import React, { useEffect } from "react";
import { EntityId } from "@reduxjs/toolkit";
import { OnChangeParams, Option, Value } from "baseui/select";
import { selectSpaceById } from "@store/space/spaceSelectors";
import useAppSelector from "@hooks/useAppSelector";
import { selectUserById } from "@store/user/userSelectors";
import NewIssueForm from "../../IssueOverview/components/NewIssueForm";
import { Frequencies, RecurringIssue } from "@store/recurringIssues/types";
import Select from "@components/base/select";
import InputField from "@components/base/input";
import DatePicker from "@components/base/datepicker";
import { addDays, isBefore, isFuture, startOfTomorrow } from "date-fns";
import { useTranslation } from "react-i18next";
import Checkbox from "@components/base/checkbox";
import { IssueType } from "@store/issue/types";
import { selectSelectedRecurringIssueId } from "@store/recurringIssues/recurringIssueSelectors";
import { isDefined } from "@helpers";
import AttachmentDrop from "@screens/IssueOverview/components/attachments/AttachmentDrop";

interface NewRecurringIssueScreenProps {
  sections: { title: string; data: { value: EntityId; label: string }[] }[];
  issue: Omit<RecurringIssue, "id">;
  setIssue: (issue: Omit<RecurringIssue, "id">) => void;
  onSubmit: () => void;
  canSubmit: boolean;
  isInEditMode: boolean;
}

const NewRecurringIssueScreen: React.FC<NewRecurringIssueScreenProps> = ({
  sections,
  issue,
  setIssue,
  onSubmit,
  canSubmit,
  isInEditMode,
}) => {
  const { t } = useTranslation();
  const {
    spaceIds,
    createdBy,
    endDate,
    startDate,
    frequency,
    interval,
    issueType,
  } = issue;
  const selectedIssueId = useAppSelector(selectSelectedRecurringIssueId);

  const minEndDate = addDays(
    isFuture(startDate) ? startDate : startOfTomorrow(),
    1,
  );

  const frequencyOptions: Value = Object.values(Frequencies).map(
    (frequency) => ({
      value: frequency,
      label: t(`frequencyTypes.${frequency}`),
    }),
  );

  const spaceLabels = useAppSelector(({ space }) =>
    spaceIds.map((id) => selectSpaceById(space, id)),
  ).map((space) => ({ id: space?.id, label: space?.label }));

  const user = useAppSelector(({ user }) => selectUserById(user, createdBy));

  const options = sections.reduce(
    (cur, section) => ({
      ...cur,
      [section.title]: section.data.filter(
        (space) => !spaceIds.includes(space.value),
      ),
    }),
    {} as Value,
  );

  const onSelectLocation = ({ value }: OnChangeParams) => {
    const options: Option[] = value.map((loc) => {
      if (loc.value) return { id: loc.value, label: loc.label };
      return loc;
    });
    setIssue({
      ...issue,
      spaceIds: options.map((loc) => loc.id).filter(isDefined),
    });
  };

  const handleIssueChange = (
    baseIssue: Pick<RecurringIssue, "label" | "description" | "reportedBy">,
  ) =>
    setIssue({
      ...issue,
      ...baseIssue,
    });

  const handleFrequencyChange = ({ value }: OnChangeParams) =>
    setIssue({
      ...issue,
      frequency: value[0]?.value,
    });

  const handleDateChange = (
    {
      date,
    }: { date: Date | Date[] | (Date | null | undefined)[] | null | undefined },
    type: "startDate" | "endDate",
  ) => {
    if (!date) return setIssue({ ...issue, [type]: null });

    const newDate = date instanceof Date ? date : date[0];
    setIssue({ ...issue, [type]: newDate });
  };

  useEffect(() => {
    if (endDate && isBefore(endDate, startDate)) {
      setIssue({ ...issue, endDate: addDays(startDate, 1) });
    }
  }, [startDate]);

  const handleIntervalChange = (value: string) => {
    const interval = parseInt(value);
    if (isNaN(interval) || interval <= 0)
      return setIssue({ ...issue, interval: 1 });
    setIssue({ ...issue, interval });
  };

  const handleEndDate = () => {
    if (endDate) return handleDateChange({ date: null }, "endDate");

    handleDateChange({ date: minEndDate }, "endDate");
  };

  const handleTypeChange = (issueType: IssueType) => {
    setIssue({
      ...issue,
      issueType,
    });
  };

  const selectedFrequency = [
    frequency === "manual"
      ? {
          id: "manual",
          label: t("frequencies.manual"),
        }
      : {
          id: frequency,
          label: t(`frequencyTypes.${frequency}`),
        },
  ];
  const imageAttachments = issue.attachment ? [issue.attachment] : [];
  return (
    <NewIssueForm
      onSelectLocation={onSelectLocation}
      locations={options}
      selectedLocations={spaceLabels}
      isInEditMode={isInEditMode}
      baseIssue={issue}
      onBaseIssueChange={handleIssueChange}
      priority
      onSubmit={onSubmit}
      user={user}
      canSubmit={canSubmit}
      issueType={issueType}
      changeType={handleTypeChange}
      isRecurringIssue>
      <div style={styles.container}>
        <AttachmentDrop
          attachments={imageAttachments}
          multiple={false}
          withVideo
          onChange={(attachments) =>
            setIssue({
              ...issue,
              attachment: attachments[0],
            })
          }
        />
        <DatePicker
          label={t("tasks.startDate")}
          date={startDate}
          onChange={(date) => handleDateChange(date, "startDate")}
          disabled={!!selectedIssueId}
          minDate={selectedIssueId ? startDate : startOfTomorrow()}
        />
        <Checkbox
          onChange={handleEndDate}
          checked={!!endDate}
          disabled={!!selectedIssueId}
          labelPlacement="right">
          <p>{t("tasks.setEndDate")}</p>
        </Checkbox>
        {endDate && (
          <DatePicker
            label={t("tasks.endDate")}
            date={endDate}
            disabled={!!selectedIssueId}
            onChange={(date) => handleDateChange(date, "endDate")}
            minDate={minEndDate}
          />
        )}
        <Select
          label={t("tasks.onFrequency")}
          options={frequencyOptions}
          disabled={!!selectedIssueId}
          value={selectedFrequency}
          multi={false}
          onChange={handleFrequencyChange}
          openInModal
        />
        <InputField
          disabled={!!selectedIssueId}
          label={t("tasks.onInterval")}
          placeholder={t("tasks.onInterval")}
          inputType="number"
          minInputValue={1}
          value={interval}
          onChangeText={handleIntervalChange}
          containerStyle={{ width: "100%" }}
        />
      </div>
    </NewIssueForm>
  );
};

const styles = {
  container: {
    height: "100%",
    width: "60%",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "flex-end",
    gap: "8px",
    marginBottom: "8px",
  },
  nextOccurrence: {
    fontStyle: "italic",
    color: "gray",
    marginBottom: "8px",
  },
};
export default NewRecurringIssueScreen;
